import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { countryNameToSlug } from "@/data/countryContent";
import { cn } from "@/lib/utils";
import { usePopularCountries } from "@/hooks/useHomepageData";
import { getFlagUrl } from "@/lib/flagUtils";

const PopularCountries = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const navigate = useNavigate();
  const { data: countries = [], isLoading: loading } = usePopularCountries();

  const handleCountryClick = (country: string) => {
    const slug = countryNameToSlug(country);
    navigate(getLocalizedPath(`/destination/${slug}`));
  };

  const skeletonWidths = [80, 100, 72, 96, 88, 112, 76, 104, 92, 84, 108, 68];

  if (loading) {
    return (
      <section className="mb-12">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-1">
            {t('home.exploreByCountry')}
          </h2>
          <p className="text-muted-foreground">
            {t('home.exploreByCountryDescription')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {skeletonWidths.map((width, i) => (
            <div
              key={i}
              className={cn(
                "h-9 rounded-full",
                "bg-gradient-to-r from-muted via-muted/60 to-muted bg-[length:200%_100%] animate-shimmer"
              )}
              style={{ width: `${width}px` }}
            />
          ))}
        </div>
      </section>
    );
  }

  if (countries.length === 0) {
    return null;
  }

  return (
    <section className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1">
          {t('home.exploreByCountry')}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t('home.exploreByCountryDescription')}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {countries.map(({ country, trip_count }, i) => (
          <div
            key={country}
            className="animate-fade-in-up"
            style={{ animationDelay: `${i * 0.03}s` }}
          >
            <Badge
              variant="outline"
              className="cursor-pointer py-2 px-4 text-sm hover:bg-primary hover:text-primary-foreground transition-colors border-border"
              onClick={() => handleCountryClick(country)}
            >
              {getFlagUrl(country) ? (
                <img 
                  src={getFlagUrl(country)!} 
                  alt="" 
                  className="mr-2 h-[15px] w-[20px] object-cover rounded-[2px]" 
                  loading="lazy"
                />
              ) : (
                <span className="mr-2 text-base">🌍</span>
              )}
              {country}
              <span className="ml-2 text-muted-foreground text-xs">({trip_count})</span>
            </Badge>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PopularCountries;
